import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import axios from "axios";
import { useProducts } from "@/entities/settings/hooks";
import { useAccount } from "@/entities/accounts/hooks";
import {
  useUpdateServiceItemsOrder,
  useDeleteServiceItem,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct
} from "./hooks";
import { ProductV2, ProductFormData, ServiceItem } from "./types";
import { productsToSectionsItems } from "./utils";
import ProductDrawer from "./components/ProductDrawer";
import ServiceItemDrawer from "./components/ServiceItemDrawer";
import EditServiceItemDrawer from "./components/EditServiceItemDrawer";
import PageLayout from "@/pages/settings/components/PageLayout";
import AddActionButton from "@/components/ui/add-action-button";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";

const TRANSLATION_KEY = "settings.product";

export const ProductsAndServicesSettings: React.FC = () => {
  const { t } = useTranslation();
  const { data: productsData, refetch: refetchProducts } = useProducts();
  const { data: accountData } = useAccount();
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();
  const updateServiceItemsOrderMutation = useUpdateServiceItemsOrder();
  const deleteServiceItemMutation = useDeleteServiceItem();

  const apiProducts = useMemo(
    () =>
      (productsData?.products ?? []).map((product: any) => ({
        id: product.id,
        name: product.name,
        description: product.description,
        serviceItems: (product.serviceItems ?? []).map((item: any) => ({
          ...item,
          productId: item.productId || product.id
        }))
      })),
    [productsData]
  );

  const [products, setProducts] = useState<ProductV2[]>([]);

  useEffect(() => {
    setProducts(apiProducts);
  }, [apiProducts]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductV2 | undefined>(
    undefined
  );
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [editingServiceItem, setEditingServiceItem] =
    useState<ServiceItem | null>(null);
  const [isDeleteServiceItemModalOpen, setIsDeleteServiceItemModalOpen] =
    useState(false);
  const [serviceItemToDelete, setServiceItemToDelete] =
    useState<ServiceItem | null>(null);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
  const [isUpdateProductModalOpen, setIsUpdateProductModalOpen] =
    useState(false);
  const [pendingProductFormData, setPendingProductFormData] =
    useState<ProductFormData | null>(null);
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);

  const handleAddProduct = () => {
    setEditingProduct(undefined);
    setIsDrawerOpen(true);
  };

  const handleEditProduct = (product: ProductV2) => {
    setEditingProduct(product);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingProduct(undefined);
  };

  const handleSaveProduct = (formData: ProductFormData) => {
    if (editingProduct) {
      setPendingProductFormData(formData);
      setPendingProductId(editingProduct.id);
      setIsUpdateProductModalOpen(true);
    } else {
      createProductMutation.mutate(
        { data: formData },
        {
          onSuccess: () => {
            refetchProducts();
            handleCloseDrawer();
          }
        }
      );
    }
  };

  const handleConfirmUpdateProduct = () => {
    if (!pendingProductId || !pendingProductFormData) return;

    updateProductMutation.mutate(
      {
        pathParams: { productId: pendingProductId },
        data: pendingProductFormData
      },
      {
        onSuccess: () => {
          refetchProducts();
          handleCloseDrawer();
        }
      }
    );
    resetUpdateProductModal();
  };

  const resetUpdateProductModal = () => {
    setIsUpdateProductModalOpen(false);
    setPendingProductFormData(null);
    setPendingProductId(null);
  };

  const handleDeleteProduct = (productId: string) => {
    deleteProductMutation.mutate(
      { pathParams: { productId } },
      {
        onSuccess: () => {
          refetchProducts();
        }
      }
    );
  };

  const handleAddServiceItem = (productId: string) => {
    setSelectedProductId(productId);
    setEditingServiceItem(null);
    setIsServiceModalOpen(true);
  };

  const handleEditServiceItem = (serviceItem: ServiceItem) => {
    setSelectedProductId(serviceItem.productId);
    setEditingServiceItem(serviceItem);
    setIsEditServiceModalOpen(true);
  };

  const handleDeleteServiceItem = (serviceItem: ServiceItem) => {
    setServiceItemToDelete(serviceItem);
    setIsDeleteConfirmed(false);
    setIsDeleteServiceItemModalOpen(true);
  };

  const resetDeleteServiceItemModal = () => {
    setIsDeleteServiceItemModalOpen(false);
    setServiceItemToDelete(null);
    setIsDeleteConfirmed(false);
  };

  const handleConfirmDeleteServiceItem = async () => {
    if (!serviceItemToDelete) return;

    try {
      await deleteServiceItemMutation.mutateAsync({
        pathParams: {
          productId: serviceItemToDelete.productId,
          serviceItemId: serviceItemToDelete.id
        }
      });
    } catch (error) {
      // 409: active revenue items still reference the service item.
      const isInUse =
        axios.isAxiosError(error) && error.response?.status === 409;
      toast.error(
        t(
          `${TRANSLATION_KEY}.deleteServiceItemModal.${
            isInUse ? "inUseError" : "deleteError"
          }`
        )
      );
    } finally {
      refetchProducts();
      resetDeleteServiceItemModal();
    }
  };

  const handleDragEndServiceItems = useCallback(
    (result: any) => {
      const { destination, source } = result;

      if (!destination) return;
      if (destination.index === source.index) return;

      const productId = result.productId;
      const product = products.find((p: ProductV2) => p.id === productId);

      if (!product || !product.serviceItems) return;

      const reorderedServiceItems = [...product.serviceItems];

      const [removed] = reorderedServiceItems.splice(source.index, 1);
      reorderedServiceItems.splice(destination.index, 0, removed);

      const updatedServiceItems = reorderedServiceItems.map(
        (item: ServiceItem, index: number) => ({
          ...item,
          itemOrder: index
        })
      );

      setProducts((prevProducts) =>
        prevProducts.map((p) =>
          p.id === productId ? { ...p, serviceItems: updatedServiceItems } : p
        )
      );

      const items: Record<number, string> = {};
      updatedServiceItems.forEach((item: ServiceItem, index: number) => {
        items[index] = item.id;
      });

      updateServiceItemsOrderMutation.mutate({
        pathParams: { productId },
        data: { items }
      });
    },
    [products, updateServiceItemsOrderMutation]
  );

  const handleSaveServiceItem = () => {
    refetchProducts();
    handleCloseServiceModal();
  };

  const handleCloseServiceModal = () => {
    setIsServiceModalOpen(false);
    setSelectedProductId("");
    setEditingServiceItem(null);
  };

  const handleCloseEditServiceModal = () => {
    setIsEditServiceModalOpen(false);
    setSelectedProductId("");
    setEditingServiceItem(null);
  };

  const productSections = productsToSectionsItems(
    products,
    handleEditProduct,
    handleAddServiceItem,
    handleEditServiceItem,
    handleDeleteServiceItem,
    handleDragEndServiceItems
  );

  return (
    <div data-test="products-settings-page">
      <PageLayout
        title={t(`${TRANSLATION_KEY}.title`)}
        subTitle={t(`${TRANSLATION_KEY}.subtitle`)}
        action={
          <AddActionButton
            data-test="products-settings-add-button"
            label={t(`${TRANSLATION_KEY}.addProduct`)}
            onClick={handleAddProduct}
          />
        }
        sections={productSections}
      />

      <ProductDrawer
        isOpen={isDrawerOpen}
        product={editingProduct}
        index={
          editingProduct
            ? Math.max(
                0,
                products.findIndex((p) => p.id === editingProduct.id)
              )
            : products.length
        }
        onClose={handleCloseDrawer}
        onSave={handleSaveProduct}
        onDelete={handleDeleteProduct}
        accountName={accountData?.name}
        totalProductCount={products.length}
      />

      {isServiceModalOpen && (
        <ServiceItemDrawer
          isOpen={isServiceModalOpen}
          productId={selectedProductId}
          serviceItem={editingServiceItem}
          onClose={handleCloseServiceModal}
          onSave={handleSaveServiceItem}
          onRefetchProducts={refetchProducts}
        />
      )}

      {editingServiceItem && (
        <EditServiceItemDrawer
          key={editingServiceItem.id}
          isOpen={isEditServiceModalOpen}
          productId={selectedProductId}
          serviceItem={editingServiceItem}
          onClose={handleCloseEditServiceModal}
          onRefetchProducts={refetchProducts}
        />
      )}

      <ConfirmationModal
        isOpen={isDeleteServiceItemModalOpen}
        setIsOpen={setIsDeleteServiceItemModalOpen}
        title={t(`${TRANSLATION_KEY}.deleteServiceItemModal.title`)}
        confirmationText={
          <>
            {t(`${TRANSLATION_KEY}.deleteServiceItemModal.description`)}
            <br />
            {t(`${TRANSLATION_KEY}.deleteServiceItemModal.descriptionLine2`)}
          </>
        }
        handleSubmit={handleConfirmDeleteServiceItem}
        handleCancel={resetDeleteServiceItemModal}
        checkboxLabel={t(
          `${TRANSLATION_KEY}.deleteServiceItemModal.checkboxLabel`
        )}
        isCheckboxRequired={true}
        onCheckboxChange={setIsDeleteConfirmed}
        isCheckboxChecked={isDeleteConfirmed}
        data-test="products-delete-service-modal"
      />

      <ConfirmationModal
        isOpen={isUpdateProductModalOpen}
        setIsOpen={setIsUpdateProductModalOpen}
        title={t(`${TRANSLATION_KEY}.updateProductModal.title`)}
        confirmationText={
          <div className="flex flex-col gap-4">
            <div>
              <div>
                {t(`${TRANSLATION_KEY}.updateProductModal.description`)}
              </div>
              <div>
                {t(`${TRANSLATION_KEY}.updateProductModal.descriptionLine2`)}
              </div>
            </div>
            {t(`${TRANSLATION_KEY}.updateProductModal.descriptionLine3`)}
          </div>
        }
        handleSubmit={handleConfirmUpdateProduct}
        handleCancel={resetUpdateProductModal}
        isPrimaryButton={true}
        submitButtonText={t(
          `${TRANSLATION_KEY}.updateProductModal.submitButton`
        )}
        data-test="products-update-product-modal"
      />
    </div>
  );
};
